import {
  selectBuildingsDamage,
  selectCurrentPlayer,
  selectHexes,
  selectPendingMovementActions,
  selectPendingRecruitmentActions,
  selectPendingSupply,
  selectPhase,
  selectPlayers,
  selectRound,
  selectSharedGameState,
  selectSpecialBuildings,
  selectUnits,
  selectWarPhaseStage
} from "../../redux/reducers/sharedStateReducer/selectors";
import {
  selectRulesActiveSectionId,
  selectShowConstructionPhaseInfoModal
} from "../../redux/reducers/privateStateReducer/selectors";

/**
 * Предикаты выполнения шага обучения. Конфиг главы ссылается на них по имени
 * (complete: {check: '<имя>', ...параметры}) — сценарии остаются декларативными
 * и редактируемыми без правок движка.
 *
 * Каждый предикат: (state, params) => boolean по ТЕКУЩЕМУ состоянию игры —
 * выполнение шага детектируется по состоянию, а не по факту действия, поэтому
 * отмена (Undo) безопасна: шаг «распройдется» сам не сможет, но и не
 * засчитается, пока состояние не достигнуто.
 */

const getPlayerByColor = (state, color) =>
  (selectPlayers(state) || []).find((player) => player.color === color) || null;

const getUnitAt = (state, hexKey) =>
  (selectUnits(state) || []).find((unit) => unit.hexKey === hexKey) || null;

export const TUTORIAL_STEP_PREDICATES = {
  // Токен на ячейке (обычные строения, дороги, кварталы)
  hexToken: (state, {hexKey, token}) => selectHexes(state)[hexKey] === token,

  // Токен исчез с ячейки (например, игрок отменил постройку)
  hexTokenGone: (state, {hexKey}) => !selectHexes(state)[hexKey],

  // Спецстроение на ячейке
  specialBuildingAt: (state, {hexKey, token}) => {
    const specialBuildings = selectSpecialBuildings(state) || {};
    const building = specialBuildings[hexKey];
    const buildingType = building && typeof building === 'object' ? building.type || building.token : building;
    return token ? buildingType === token : Boolean(building);
  },

  // Спецстроение исчезло (разрушено)
  specialBuildingGone: (state, {hexKey}) => {
    const specialBuildings = selectSpecialBuildings(state) || {};
    return !specialBuildings[hexKey];
  },

  // Стартовый квартал игрока поставлен на ячейку (фаза SETUP)
  startingQuarterAt: (state, {hexKey, color}) => {
    const player = getPlayerByColor(state, color);
    if (!player) return false;
    const startingQuarters = selectSharedGameState(state).startingQuarters || {};
    return startingQuarters[player.id] === hexKey;
  },

  // На ячейке стоит отряд (опционально: цвет владельца, тип, мин. размер)
  unitAt: (state, {hexKey, color, type, minSize}) => {
    const unit = getUnitAt(state, hexKey);
    if (!unit) return false;
    if (color) {
      const player = getPlayerByColor(state, color);
      if (!player || unit.ownerId !== player.id) return false;
    }
    if (type && unit.type !== type) return false;
    return !(minSize && unit.size < minSize);
  },

  // Отряд НАНЯТ на ячейке: и отряд существует, и найм записан в pending-историю.
  // Проверять только unitAt нельзя: найм — это ПАРА диспатчей (RECRUIT_UNIT +
  // ADD_PENDING_RECRUITMENT_ACTION), и продвижение шага между ними оставило бы
  // вторую половину пары заблокированной новым шагом
  unitRecruitedAt: (state, {hexKey, type}) => {
    const unit = getUnitAt(state, hexKey);
    if (!unit) return false;
    if (type && unit.type !== type) return false;
    const pendingRecruitmentActions = selectPendingRecruitmentActions(state) || [];
    return pendingRecruitmentActions.some(
      (recruitment) => recruitment.unitId === unit.id && recruitment.actionType === 'UNIT_RECRUITMENT'
    );
  },

  // Отряд ПЕРЕМЕЩЕН на ячейку: и стоит там, и перемещение записано в pending
  // (та же причина парности: MOVE_UNIT + ADD_PENDING_MOVEMENT_ACTION)
  unitMovedTo: (state, {toHexKey, color}) => {
    const unit = getUnitAt(state, toHexKey);
    if (!unit) return false;
    if (color) {
      const player = getPlayerByColor(state, color);
      if (!player || unit.ownerId !== player.id) return false;
    }
    const pendingMovementActions = selectPendingMovementActions(state) || [];
    return pendingMovementActions.some(
      (movement) => movement.unitId === unit.id && movement.toHexKey === toHexKey
    );
  },

  // На ячейке нет отряда (уничтожен/ушел)
  noUnitAt: (state, {hexKey}) => !getUnitAt(state, hexKey),

  // Отряд на ячейке получил снабжение (pending; count — минимум единиц)
  unitSuppliedAt: (state, {hexKey, count = 1}) => {
    const unit = getUnitAt(state, hexKey);
    if (!unit) return false;
    const pendingSupply = selectPendingSupply(state) || [];
    const suppliedCount = pendingSupply.filter((supplyAction) => supplyAction.unitId === unit.id).length;
    return suppliedCount >= count;
  },

  // Отряд на ячейке докуплен по размеру (pending SIZE_RECRUITMENT; count — минимум докупок)
  unitSizeRecruitedAt: (state, {hexKey, count = 1}) => {
    const unit = getUnitAt(state, hexKey);
    if (!unit) return false;
    const pendingRecruitmentActions = selectPendingRecruitmentActions(state) || [];
    const actionsCount = pendingRecruitmentActions
      .filter((recruitment) => recruitment.unitId === unit.id && recruitment.actionType === 'SIZE_RECRUITMENT')
      .length;
    return actionsCount >= count;
  },

  // Игрок выбрал (кликнул) каждый из отрядов на перечисленных ячейках В РАМКАХ
  // текущего шага (выборы копятся в tutorial.inspectedUnitIds и сбрасываются
  // при продвижении) — «изучи детальный расчет урона каждого участника боя».
  // Отряд, которого на ячейке уже нет, пропускается — шаг не должен зависнуть
  unitsInspected: (state, {hexKeys}) => {
    const inspectedUnitIds = state.private?.tutorial?.inspectedUnitIds || [];
    return (hexKeys || []).every((hexKey) => {
      const unit = getUnitAt(state, hexKey);
      if (!unit) return true;
      return inspectedUnitIds.includes(unit.id);
    });
  },

  // Выстрел по цели сделан и ВИСИТ в pending (применение — при завершении
  // этапа стрельбы): шаг «выстрели» завершается сразу после выстрела, еще на
  // этапе стрельбы — следующий шаг успевает показать свою модалку до
  // завершения этапа
  shotPendingAt: (state, {targetHexKey}) =>
    (selectSharedGameState(state).pendingShootingActions || []).some(
      (shootingAction) => shootingAction.targetHexKey === targetHexKey),

  // Игрок инспектировал ячейку НА ТЕКУЩЕМ шаге (клик по ячейке -> панель
  // подробного описания; копится в tutorial.inspectedHexKeys, сброс при
  // продвижении — порядок «сначала храм, потом склад» дают два шага подряд)
  hexInspected: (state, {hexKey}) =>
    (state.private?.tutorial?.inspectedHexKeys || []).includes(hexKey),

  // Игрок открыл карточку фракции из КАЖДОГО перечисленного источника в
  // рамках текущего шага (копится в tutorial.factionCardOpenSources, сброс
  // при продвижении) и уже закрыл карточку — следующая модалка не наложится
  factionCardViewedFrom: (state, {sources}) => {
    const tutorialState = state.private?.tutorial || {};
    const openedSources = tutorialState.factionCardOpenSources || [];
    return !tutorialState.isFactionCardOverlayOpen
      && (sources || []).every((source) => openedSources.includes(source));
  },

  // Аналог factionCardViewedFrom для карточки ПОЛИТИКИ (плашка в панели
  // игроков / иконка политики на кнопке завершения хода)
  policyCardViewedFrom: (state, {sources}) => {
    const tutorialState = state.private?.tutorial || {};
    const openedSources = tutorialState.policyCardOpenSources || [];
    return !tutorialState.isPolicyCardOverlayOpen
      && (sources || []).every((source) => openedSources.includes(source));
  },

  // Сверху нужная боковая панель И правила закрыты — панель (а на панели
  // списка игроков — задачи обучения) реально видна пользователю. Верхнюю
  // панель в стор зеркалит SideMenuStackContext (sideMenuBroughtToFront)
  sideMenuOnTop: (state, {sideMenuId}) =>
    state.private?.tutorial?.topSideMenuId === sideMenuId
    && !selectShowConstructionPhaseInfoModal(state),

  // Открыт нужный раздел правил (модалка правил открыта, активный раздел
  // совпадает; раздел в стор пишет RulesModal — см. setRulesActiveSection)
  rulesSectionOpened: (state, {sectionId}) =>
    Boolean(selectShowConstructionPhaseInfoModal(state))
    && selectRulesActiveSectionId(state) === sectionId,

  // Игрок пролистал страницу нужного раздела правил вниз. Проверяется только
  // накопитель шага: наполняет его подсказка внутри ОТКРЫТЫХ правил
  // (TutorialRulesScrollHint), так что открытость модалки требовать незачем —
  // иначе шаг не проходил бы в headless-пробнике, который закрывает правила в
  // том же тике
  rulesSectionScrolled: (state, {sectionId}) =>
    (state.private?.tutorial?.scrolledRulesSectionIds || []).includes(sectionId),

  // Игрок цвета перебросил кости в текущем ближнем бою (демо переброса за
  // дополнительное снабжение)
  meleeRerolledBy: (state, {color}) => {
    const player = getPlayerByColor(state, color);
    if (!player) return false;
    const pendingMeleeDamage = selectSharedGameState(state).pendingMeleeDamage || [];
    return pendingMeleeDamage.some(
      (damageAction) => damageAction.ownerId === player.id && damageAction.hasRerolled
    );
  },

  // Отряд СМАНЕВРИРОВАЛ на ячейку (пара MOVE_UNIT + ADD_PENDING_MANEUVER_ACTION —
  // та же причина парности, что у unitMovedTo)
  unitManeuveredTo: (state, {toHexKey, color}) => {
    const unit = getUnitAt(state, toHexKey);
    if (!unit) return false;
    if (color) {
      const player = getPlayerByColor(state, color);
      if (!player || unit.ownerId !== player.id) return false;
    }
    const pendingManeuverActions = selectSharedGameState(state).pendingManeuverActions || [];
    return pendingManeuverActions.some(
      (maneuver) => maneuver.unitId === unit.id && maneuver.toHexKey === toHexKey
    );
  },

  // Игровой момент: фаза/этап/раунд/чей ход. Основной предикат await-шагов
  // («дождись хода противника», «начался этап стрельбы»)
  gameMoment: (state, {phase, stage, minRound, currentPlayerColor, currentPlayerIsBot}) => {
    if (phase && selectPhase(state) !== phase) return false;
    if (stage && selectWarPhaseStage(state) !== stage) return false;
    if (minRound && selectRound(state) < minRound) return false;
    const currentPlayer = selectCurrentPlayer(state);
    if (currentPlayerColor && currentPlayer?.color !== currentPlayerColor) return false;
    return !(currentPlayerIsBot !== undefined && Boolean(currentPlayer?.isBot) !== currentPlayerIsBot);
  },

  // Город принадлежит цвету (по токену центрального квартала)
  cityOwnedBy: (state, {centralHexKey, color}) =>
    selectHexes(state)[centralHexKey] === `central_quarter_${color}`,

  // Политика куплена игроком (активна или куплена и ждет активации)
  policyBought: (state, {color, policyId}) => {
    const player = getPlayerByColor(state, color);
    if (!player) return false;
    return player.policy?.id === policyId || player.nextPolicy?.id === policyId;
  },

  // Инициатива перехвачена игроком цвета
  initiativeInterceptedBy: (state, {color}) => {
    const player = getPlayerByColor(state, color);
    if (!player) return false;
    return selectSharedGameState(state).nextInitiativePlayerId === player.id;
  },

  // Повреждения строения на ячейке достигли min (крепость под обстрелом)
  buildingDamageAtLeast: (state, {hexKey, min = 1}) => {
    const buildingsDamage = selectBuildingsDamage(state) || {};
    return (buildingsDamage[hexKey] || 0) >= min;
  },

  // Комбинатор: все вложенные предикаты истинны
  allOf: (state, {checks}) => checks.every((check) => checkTutorialPredicate(state, check)),
};

/**
 * Проверка предиката по декларации из конфига: {check: имя, ...параметры}
 */
export const checkTutorialPredicate = (state, declaration) => {
  if (!declaration) return false;
  const predicate = TUTORIAL_STEP_PREDICATES[declaration.check];
  if (!predicate) {
    console.error(`[Tutorial] Unknown step predicate: ${declaration.check}`);
    return false;
  }
  return predicate(state, declaration);
};
